import { Link } from "@tanstack/react-router";
import { Icon } from "@majourneys/icons";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { ShelfArrow, useShelfScroll } from "../../../shared/ui/scroll-shelf";
import { useCategories } from "../hooks/use-categories";
import { STAY_PROPERTY_TYPES, isStayPropertyType } from "@majourneys/shared";
import { useStayTypeCounts } from "../../stays/api/stays-query";
import { STAY_ICON_VERSION, stayTypeIconSrc } from "../../stays/lib/stay-type-icon";

interface SubcategoryBarProps {
  categorySlug?: string;
  citySlug?: string;
  lang?: string;
  /**
   * The page's current search, carried onto the hotel chips only. Switching
   * from riads to hotels must keep the dates and party the guest already
   * gave us, or they land on a list with no prices on it.
   */
  search?: Record<string, unknown>;
}

interface CategoryRow {
  id: string;
  slug: string;
  name: string;
  icon?: string;
  parentSlug?: string | null;
  /** Published places directly in this type (in the city, on a city hub). */
  itemsCount?: number;
}

export function SubcategoryBar({
  categorySlug,
  citySlug,
  lang = "en",
  search,
}: SubcategoryBarProps) {
  const { t } = useTranslation();
  const { data: response } = useCategories(citySlug);
  const raw = response.data as CategoryRow[] | undefined;
  // Hooks before any early return. Only the Stays row needs the hotel counts.
  const stayRow =
    !!categorySlug &&
    (categorySlug === "stays" ||
      isStayPropertyType(categorySlug) ||
      raw?.find((c) => c.slug === categorySlug)?.parentSlug === "stays");
  const stayCounts = useStayTypeCounts(stayRow, citySlug);
  if (!raw || !categorySlug) return null;

  /**
   * A hotel type is a category slug to the router but (villa aside) not a row
   * in `categories` — those properties live in their own tables. Without this
   * the lookup below fails and the whole row disappears on /category/hotel,
   * taking Camp with it and leaving the guest no way back to what they were
   * just browsing.
   */
  const stayType = isStayPropertyType(categorySlug) ? categorySlug : undefined;

  const current = raw.find((c) => c.slug === categorySlug);
  if (!current && !stayType) return null;
  const parentSlug = stayType ? "stays" : (current?.parentSlug ?? current?.slug);
  const activeSlug = current?.parentSlug ? current.slug : undefined;
  /**
   * A category that is also a stay property type is dropped here and rendered
   * by HotelTypeChips instead, so it appears once.
   *
   * `villa` is the case: the row survives in `categories` from the partner
   * villas we no longer sell, and without this the guest sees two Villas chips
   * side by side pointing at the same page.
   */
  const children = raw.filter(
    (c) => c.parentSlug === parentSlug && !isStayPropertyType(c.slug),
  );

  /**
   * Hotels belong in this row, not below the results.
   *
   * They are not an activity subcategory, so they are not in `children`: they
   * live in their own tables because they are charged online while everything
   * else here is paid on arrival. But this row is where a guest looks to
   * narrow down what kind of stay they want, and the explanatory panel sits
   * below 12 villa cards and the pagination, where the owner himself could not
   * find it. So the entry point goes here and the panel keeps the explanation.
   */
  const showHotels = parentSlug === "stays";
  if (children.length === 0 && !showHotels) return null;

  const categoryTiles = children.map((c) => {
    const active = activeSlug === c.slug;
    const className = TILE_LINK;
    // In the Stays row, Camps reads plural like the hotel types beside it.
    const label = showHotels && c.slug === "camp" ? t("stays.camps") : c.name;
    const content = (
      <TileBody active={active} label={label} icon={<SubcategoryIcon slug={c.slug} />} />
    );
    const el = citySlug ? (
        <li key={c.id}>
          <Link
            to="/$lang/category/$category/$city"
            params={{ lang, category: c.slug, city: citySlug }}
            resetScroll={false}
            className={className}
            aria-current={active ? "page" : undefined}
          >
            {content}
          </Link>
        </li>
    ) : (
      <li key={c.id}>
        <Link
          to="/$lang/category/$category"
          params={{ lang, category: c.slug }}
          resetScroll={false}
          className={className}
          aria-current={active ? "page" : undefined}
        >
          {content}
        </Link>
      </li>
    );
    return { key: c.slug, name: label, count: c.itemsCount ?? 0, el };
  });

  /*
   * Busiest first (owner, 2026-09-26: "always sort them by numbers of
   * items"), with "All" kept in front. On Stays the hotel types join the same
   * sort, counted from the supplier's properties, so Hotels can come before
   * Camp when there are more of them. Ties keep alphabetical order.
   *
   * Except Camps, which sits right after "All" on Stays, as in the app.
   */
  const hotelTiles = showHotels ? stayTypeTiles({ lang, activeType: stayType, search, counts: stayCounts, t }) : [];
  const pinFirst = (key: string) => (showHotels && key === "camp" ? 0 : 1);
  const tiles = [...categoryTiles, ...hotelTiles].sort(
    (a, b) => pinFirst(a.key) - pinFirst(b.key) || b.count - a.count || a.name.localeCompare(b.name),
  );

  return (
    <SubcategoryRail label={t("activity.subcategories_aria")} rtl={lang === "ar"}>
      <li>
        <AllChip lang={lang} parentSlug={parentSlug!} citySlug={citySlug} active={!activeSlug && !stayType} label={t("common.all", { defaultValue: "All" })} />
      </li>
      {tiles.map((tile) => tile.el)}
    </SubcategoryRail>
  );
}

/**
 * The sideways row of type tiles, with arrows on a desk.
 *
 * At 1024 to 1920 the row runs past the edge ("Wate..." under the fade) and a
 * mouse has no sideways swipe, so without arrows the last types were out of
 * reach. The arrows are the shelf arrows the city hub rows use, laid over the
 * row's ends, and each one is shown only while there is somewhere to go.
 * Phones keep the swipe and get no arrows (below `sm`).
 *
 * Its own component so the scroll hook mounts with the row it measures: the
 * bar above returns early on pages with no types.
 */
function SubcategoryRail({
  label,
  rtl,
  children,
}: {
  label: string;
  rtl: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const shelf = useShelfScroll({ step: 240 });
  /* The far edge fades so the row reads as one that scrolls sideways, and the
     near edge fades too once it has been scrolled, so a tile never ends in a
     hard cut under the back arrow. */
  const fade = `linear-gradient(${rtl ? "270deg" : "90deg"}, ${
    shelf.canScrollBack ? "transparent 0, #000 48px" : "#000 0"
  }, ${shelf.canScrollForward ? "#000 calc(100% - 40px), transparent" : "#000 100%"})`;
  /* Centred on the 60px icon tile (8px of padding above it), not the label. */
  const arrow = "absolute top-[22px] z-[1] hidden sm:grid shadow-[0_4px_14px_rgba(4,0,51,0.10)]";

  return (
    <nav aria-label={label} className="relative">
      {/*
        `-mb-4 pb-6` buys the selected chip's shadow room to finish. See the
        same pattern and the reasoning in category-bar.tsx: this row scrolls
        sideways, which clips it vertically too. The negative margin hands the
        extra padding back to the layout so nothing below moves.
      */}
      <div
        ref={shelf.ref}
        className="-mx-4 -mb-4 overflow-x-auto px-4 pb-6 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ WebkitMaskImage: fade, maskImage: fade }}
      >
        <ul className="m-0 flex min-w-max list-none gap-3 p-0 pe-10 pt-1">{children}</ul>
      </div>
      {shelf.canScrollBack && (
        <ShelfArrow
          label={t("common.scroll_back", { defaultValue: "Scroll back" })}
          icon={rtl ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"}
          onClick={shelf.scrollBack}
          className={`${arrow} -start-3`}
        />
      )}
      {shelf.canScrollForward && (
        <ShelfArrow
          label={t("common.scroll_forward", { defaultValue: "Scroll forward" })}
          icon={rtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}
          onClick={shelf.scrollForward}
          className={`${arrow} -end-3`}
        />
      )}
    </nav>
  );
}

/**
 * One chip per hotel type, in the row where a guest chooses what kind of stay
 * they want. Riads next to Villas is the comparison they are actually making.
 *
 * They lead to /category/{type}, the same shape of URL as every other chip in
 * this row, and the listing opens in place. It used to lead out to
 * /stays/{type} — a second URL family for what a guest experiences as one
 * catalogue, so clicking Hotels read as being thrown off the page. What still
 * differs is the payment model, and the destination page says so above its
 * first card rather than leaving the URL to carry the warning.
 *
 * Rendered only when some city has a published hotel, so a chip can never
 * open an empty page.
 */

/**
 * The hotel types as tiles, beside Camp in the Stays row. Only once some city
 * has a published property, so a tile can never open an empty catalogue.
 */
function stayTypeTiles({
  lang,
  activeType,
  search,
  counts,
  t,
}: {
  lang: string;
  activeType?: string;
  search?: Record<string, unknown>;
  counts: Map<string, number> | null;
  t: (key: string) => string;
}) {
  if (!counts || ![...counts.values()].some((n) => n > 0)) return [];
  return STAY_PROPERTY_TYPES.map((type) => {
    const active = type === activeType;
    const name = t(`stays.types.${type}`);
    return {
      key: type,
      name,
      count: counts.get(type) ?? 0,
      el: (
        <li key={type}>
          <Link
            to="/$lang/category/$category"
            params={{ lang, category: type }}
            resetScroll={false}
            search={(search ?? {}) as never}
            aria-current={active ? "page" : undefined}
            className={TILE_LINK}
          >
            <TileBody active={active} label={name} icon={<StayTypeIcon type={type} />} />
          </Link>
        </li>
      ),
    };
  });
}

/**
 * The stay types get their own icon, not the one line hotel glyph they shared.
 *
 * One glyph for all five said nothing: Riads, Resorts and Villas sat in a row
 * looking identical, so the icon was decoration rather than a way to tell them
 * apart. These are photoreal miniatures, the house style for a category icon
 * (the navy and gold clay is the illustration style, not this one).
 *
 * They were drawn for their OUTLINES, because at this size a hotel, a villa
 * and a guesthouse are otherwise the same brown rectangle: a dark tower, a
 * terracotta keyhole arch, a thatched pavilion under a palm, a white block
 * behind a turquoise pool, a narrow ochre house with a blue door. Each was
 * checked by shrinking it to 26px and magnifying, which is the only test that
 * matters here. A replacement has to pass the same one.
 */
function StayTypeIcon({ type }: { type: string }) {
  return (
    <img
      src={stayTypeIconSrc(type)}
      alt=""
      aria-hidden
      loading="lazy"
      // The buildings are detailed miniatures, so they get most of the tile.
      className="h-[52px] w-[52px] object-contain"
    />
  );
}

// 3D clay subcategory icons (mirrored from the mobile app). PNGs live in
// apps/majourneys/public/images/subcategories/sub-<slug>.png. Keep this set in
// sync with `subcategoryIcons` in packages/assets/src/images/index.ts.
const CLAY_ICON_SLUGS = new Set([
  "air",
  "buggy",
  "calligraphy-painting",
  "camel",
  "camp",
  "city-tour",
  "cooking-class",
  "crafts",
  "day-trips",
  "diving",
  "horse",
  "jet-ski",
  "pottery",
  "quad",
  "shows",
  "villa",
  "water",
  "weaving",
  "zellige",
]);

// Bumped whenever a sub-*.png is redrawn. The images are cached for 30 days
// (see stay-type-icon.ts), so without a new query string returning visitors
// keep the old drawing for a month. 2026-09-26: camel, horse, quad, buggy and
// air redrawn.
const SUB_ICON_VERSION = "2026-09-26";

// Category slugs that differ from the icon filename.
const CLAY_ICON_ALIAS: Record<string, string> = { jetski: "jet-ski" };

function SubcategoryIcon({ slug }: { slug: string }) {
  const key = slug.toLowerCase().replace(/\s+/g, "-");
  const clayKey = CLAY_ICON_ALIAS[key] ?? key;

  if (CLAY_ICON_SLUGS.has(clayKey)) {
    return (
      <img
        // Camp was redrawn with the stay types, so it carries their version
        // (see stay-type-icon.ts on the 30-day image cache).
        src={`/images/subcategories/sub-${clayKey}.png?v=${clayKey === "camp" ? STAY_ICON_VERSION : SUB_ICON_VERSION}`}
        alt=""
        aria-hidden
        loading="lazy"
        // Camp is drawn as a building miniature like the hotel types beside
        // it in the Stays row, so it takes their size.
        className={clayKey === "camp" ? "h-[52px] w-[52px] object-contain" : "h-11 w-11 object-contain"}
      />
    );
  }

  return <DefaultIcon />;
}

function DefaultIcon() {
  return (
    <svg
      className="h-6 w-6 text-[#110E39]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3Z" />
      <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z" />
    </svg>
  );
}

/** "All" of the parent: the first chip, navy when no type is picked. */
function AllChip({
  lang,
  parentSlug,
  citySlug,
  active,
  label,
}: {
  lang: string;
  parentSlug: string;
  citySlug?: string;
  active: boolean;
  label: string;
}) {
  const className = TILE_LINK;
  return citySlug ? (
    <Link to="/$lang/category/$category/$city" params={{ lang, category: parentSlug, city: citySlug }} resetScroll={false} className={className} aria-current={active ? "page" : undefined}>
      <TileBody active={active} label={label} icon={<Icon name="ri-layout-grid-line" className="text-[26px] text-[#110E39]" aria-hidden="true" />} />
    </Link>
  ) : (
    <Link to="/$lang/category/$category" params={{ lang, category: parentSlug }} resetScroll={false} className={className} aria-current={active ? "page" : undefined}>
      <TileBody active={active} label={label} icon={<Icon name="ri-layout-grid-line" className="text-[26px] text-[#110E39]" aria-hidden="true" />} />
    </Link>
  );
}

/* Types as big 3D icon tiles (owner, 2026-09-25): the clay icon on a white
   tile, the name under it. The chosen one gets a navy ring.

   72px wide, or as wide as the label's longest word when that is wider
   (`w-min` is the min-content width, the longest word). The label wraps only
   between words: "Calligraphy" is 74px at this size and used to be chopped
   into "Calligraph / y" on a phone. */
const TILE_LINK =
  "group flex w-min min-w-[72px] flex-col items-center gap-1.5 no-underline active:scale-[0.96] transition-transform duration-150";

function TileBody({ icon, label, active }: { icon: React.ReactNode; label: string; active: boolean }) {
  return (
    <>
      <span
        className="flex h-[60px] w-[60px] items-center justify-center rounded-[18px] bg-white transition-[transform,box-shadow] duration-200 group-hover:-translate-y-[2px]"
        style={{
          // Chosen shows as the navy ring; the soft rest shadow stays the
          // same in both states, never deeper (no shadow marks a choice).
          boxShadow: active
            ? "0 0 0 2px #110E39, 0 1px 2px rgba(17,14,57,0.06), 0 6px 14px -6px rgba(17,14,57,0.16)"
            : "0 1px 2px rgba(17,14,57,0.06), 0 6px 14px -6px rgba(17,14,57,0.16)",
        }}
      >
        {icon}
      </span>
      <span
        className="max-w-full text-center hyphens-manual [text-wrap:balance] text-[12.5px] leading-tight"
        style={{ color: active ? "#110E39" : "#5a5d77", fontWeight: active ? 700 : 500 }}
      >
        {label}
      </span>
    </>
  );
}
